/*
 * ASHEIS favorites (wishlist).
 *
 * Strategy (see shopify_favorites_service DEPLOY.md):
 * - localStorage is the instant render source — hearts paint without waiting
 *   on the network, for guests and logged-in customers alike.
 * - Logged-in: the customer metafield list (rendered into the page by
 *   snippets/favorites-data.liquid) is authoritative. Toggles POST explicit
 *   add/remove to the /apps/favorites app proxy (guests would get 401), and
 *   every response's `favorites` array resyncs the local cache.
 * - Guest: localStorage only; hearting shows the "log in to save" prompt.
 *   Guest-added favorites are tracked as `pending` and pushed to the backend
 *   on the first page load after login, so the prompt's promise holds.
 *
 * Conflict rule: once a customer has synced on this device (store.owner ==
 * customerId), the server list is mirrored as-is on load — guest-era pending
 * adds are pushed, but removals done on other devices are respected (we never
 * re-add from a stale cache).
 */
(function () {
  var KEY = 'asheis:favorites:v1';

  var cfgEl = document.querySelector('[data-favorites-config]');
  if (!cfgEl) return;
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  function readStore() {
    try {
      var s = JSON.parse(localStorage.getItem(KEY));
      if (s && typeof s === 'object') return s;
    } catch (e) { /* corrupt or unavailable */ }
    return null;
  }
  function writeStore() {
    try { localStorage.setItem(KEY, JSON.stringify(store)); } catch (e) { /* private mode etc. */ }
  }

  var store = readStore() || { owner: 'guest', products: [], articles: [], pending: { products: [], articles: [] } };
  store.products = store.products || [];
  store.articles = store.articles || [];
  store.pending = store.pending || { products: [], articles: [] };
  store.pending.products = store.pending.products || [];
  store.pending.articles = store.pending.articles || [];

  function listKey(kind) { return kind === 'article' ? 'articles' : 'products'; }
  function gidToId(gid) { return String(gid).split('/').pop(); }

  function has(kind, id) { return store[listKey(kind)].indexOf(String(id)) !== -1; }

  function setLocal(kind, id, on) {
    var k = listKey(kind);
    id = String(id);
    store[k] = store[k].filter(function (x) { return x !== id; });
    if (on) store[k].push(id);
    if (!cfg.loggedIn) {
      store.pending[k] = store.pending[k].filter(function (x) { return x !== id; });
      if (on) store.pending[k].push(id);
    }
    writeStore();
  }

  function render() {
    var buttons = document.querySelectorAll('[data-favorite-toggle]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var on = has(btn.getAttribute('data-favorite-kind'), btn.getAttribute('data-favorite-id'));
      btn.classList.toggle('is-favorited', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }

  function post(kind, id, action) {
    return fetch(cfg.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: kind, id: String(id), action: action })
    }).then(function (r) {
      if (!r.ok) throw new Error('favorites: HTTP ' + r.status);
      return r.json();
    }).then(function (data) {
      if (data && data.favorites) {
        // The response list is authoritative for this kind — resync the cache.
        store[listKey(kind)] = data.favorites.map(gidToId);
        writeStore();
        render();
      }
      return data;
    });
  }

  /* ---- guest login prompt ------------------------------------------- */

  var prompt = document.querySelector('[data-favorites-login-prompt]');
  var promptTimer = null;

  function showPrompt() {
    if (!prompt || cfg.loggedIn) return;
    clearTimeout(promptTimer);
    prompt.hidden = false;
    void prompt.offsetWidth; // flush styles so the transition runs from the hidden state
    prompt.classList.add('is-visible');
    promptTimer = setTimeout(hidePrompt, 6000);
  }
  function hidePrompt() {
    if (!prompt) return;
    clearTimeout(promptTimer);
    prompt.classList.remove('is-visible');
    promptTimer = setTimeout(function () { prompt.hidden = true; }, 300);
  }

  /* ---- interactions --------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    var dismiss = target.closest('[data-favorites-login-dismiss]');
    if (dismiss) { hidePrompt(); return; }

    var btn = target.closest('[data-favorite-toggle]');
    if (!btn) return;

    var kind = btn.getAttribute('data-favorite-kind');
    var id = btn.getAttribute('data-favorite-id');
    var on = !has(kind, id);

    setLocal(kind, id, on); // optimistic — instant feedback
    render();

    if (cfg.loggedIn) {
      post(kind, id, on ? 'add' : 'remove').catch(function (err) {
        console.warn('[favorites]', err); // keep the optimistic local state
      });
    } else if (on) {
      showPrompt();
    }
  });

  /* ---- favorites listing pages ----------------------------------------- */
  /* Shell grids ([data-favorites-grid], rendered by sections/favorites-*.liquid)
     are filled by fetching the same section through the search section-rendering
     API with an `id:A OR id:B` query — the recently-viewed-products pattern.
     Ids come from the (already synced) local store, newest first. */

  /* Same card entrance the collection page plays (ProductList.reveal in
     theme.js: opacity 0→1, 20px rise, 0.2s, 0.05s stagger after a 0.4s
     start) — used on first paint of the grids and when the SINGLE/GRID
     toggle switches layout. */
  function animateGridReveal(grid) {
    var cards = grid.querySelectorAll('.product-card, .blog-post-card');
    if (!cards.length || !cards[0].animate) return;
    Array.prototype.forEach.call(cards, function (card, i) {
      card.animate(
        [
          { opacity: 0, transform: 'translateY(20px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ],
        { duration: 200, delay: 400 + i * 50, easing: 'ease-in-out', fill: 'backwards' }
      );
    });
  }

  // SINGLE/GRID radio toggles (favorites pages + the styling listing) replay
  // the reveal, matching the collection page's layout-switch behaviour.
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || (input.name !== 'favorites-view' && input.name !== 'styling-view')) return;
    var sectionEl = input.closest('.shopify-section');
    if (!sectionEl) return;
    var grid = sectionEl.querySelector('[data-favorites-grid], .product-list, .blog-post-list');
    if (grid) animateGridReveal(grid);
  });

  function renderFavoritesPage() {
    var grids = document.querySelectorAll('[data-favorites-grid]');
    if (!grids.length) return;

    Array.prototype.forEach.call(grids, function (grid) {
      var kind = grid.getAttribute('data-favorites-grid');
      var ids = store[listKey(kind)].slice().reverse(); // newest hearted first
      var sectionEl = grid.closest('.shopify-section');
      var emptyEl = sectionEl && sectionEl.querySelector('[data-favorites-empty]');
      if (!sectionEl) return;

      if (!ids.length) {
        if (emptyEl) emptyEl.hidden = false;
        return;
      }

      var sectionId = sectionEl.id.replace('shopify-section-', '');
      var chunks = [];
      for (var i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10)); // stay under search page size

      Promise.all(chunks.map(function (chunk) {
        var q = chunk.map(function (id) { return 'id:' + id; }).join(' OR ');
        var url = '/search?section_id=' + encodeURIComponent(sectionId) + '&type=' + encodeURIComponent(kind) + '&q=' + encodeURIComponent(q);
        return fetch(url).then(function (r) {
          if (!r.ok) throw new Error('favorites page: HTTP ' + r.status);
          return r.text();
        }).then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var results = doc.querySelector('[data-favorites-results]');
          return results ? results.innerHTML : '';
        });
      })).then(function (parts) {
        var html = parts.join('');
        if (html.replace(/\s/g, '') !== '') {
          grid.innerHTML = html;
          animateGridReveal(grid); // entrance fade, as on the collection page
        } else if (emptyEl) {
          emptyEl.hidden = false;
        }
      }).catch(function (err) {
        console.warn('[favorites]', err);
        if (emptyEl) emptyEl.hidden = false;
      });
    });
  }

  /* ---- initial sync ---------------------------------------------------- */

  function init() {
    if (!cfg.loggedIn) {
      render();
      renderFavoritesPage();
      return;
    }

    var server = {
      products: (cfg.products || []).map(String),
      articles: (cfg.articles || []).map(String)
    };

    // Guest-era favorites not yet on the server get pushed once after login.
    var pushes = [];
    ['products', 'articles'].forEach(function (k) {
      var kind = k === 'products' ? 'product' : 'article';
      var pend = store.pending[k].filter(function (id) {
        return store[k].indexOf(id) !== -1 && server[k].indexOf(id) === -1;
      });
      pend.forEach(function (id) { pushes.push([kind, id]); });
      store[k] = server[k].concat(pend); // mirror server + optimistic pendings
    });

    store.owner = String(cfg.customerId);
    store.pending = { products: [], articles: [] };
    writeStore();
    render();
    renderFavoritesPage();

    pushes.reduce(function (chain, t) {
      return chain.then(function () { return post(t[0], t[1], 'add'); });
    }, Promise.resolve()).catch(function (err) {
      console.warn('[favorites] login sync', err);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
